import { resolveCode } from '../core/Input.js';
import { clamp } from '../utils/math.js';
import { isConfigured, submitScore } from '../cloud/scores.js';
import { Leaderboard } from './Leaderboard.js';
import { SettingsPanel } from './SettingsPanel.js';

/**
 * Boot, menu, pause and game-over screens. The UI is driven entirely by
 * `setState`, which keeps game-state transitions in one place
 * (Game.setState).
 */
export class UI {
  constructor({ meta, settings = null }) {
    this.meta = meta;
    this.settings = settings;
    this.state = 'boot';
    this.onPrimary = null;

    this.boot = document.getElementById('boot');
    this.bootTitle = document.getElementById('boot-title');
    this.bootStatus = document.getElementById('boot-status');
    this.bootProgress = document.getElementById('boot-progress');
    this.bootBar = document.getElementById('boot-bar');
    this.bootDetail = document.getElementById('boot-detail');

    this.menu = document.getElementById('menu');
    this.menuState = document.getElementById('menu-state');
    this.menuEyebrow = document.getElementById('menu-eyebrow');
    this.menuTitle = document.getElementById('menu-title');
    this.menuSubtitle = document.getElementById('menu-subtitle');
    this.menuNote = document.getElementById('menu-note');
    this.primaryButton = document.getElementById('primary-button');
    this.shareButton = document.getElementById('share-button');
    this.menuScore = document.getElementById('menu-score');
    this.menuScoreValue = document.getElementById('menu-score-value');
    this.menuScoreReason = document.getElementById('menu-score-reason');
    this.playerNameRow = document.getElementById('player-name-row');
    this.playerNameInput = document.getElementById('player-name-input');
    this.leaderboardButton = document.getElementById('leaderboard-button');
    this.leaderboardRank = document.getElementById('leaderboard-rank');
    this.settingsButton = document.getElementById('settings-button');
    this.hud = document.getElementById('hud');

    this._lastScore = 0;
    this._lastReason = '';
    this._playerKey = 'k-town-craft:player-name';
    // The name persists across runs; the first run has no stored name.
    this.playerName = this.#readPlayerName();
    // Pre-rendered share card, so the Share button can hand the picture to
    // navigator.share() without an async gap.
    this._cardBlob = null;
    this._cardKey = '';

    // Leaderboard: the id of the run posted from the last game over, and the
    // rank it earned, so the board can point the player at their own row.
    this.board = new Leaderboard({ onClose: () => this.#closeBoard() });
    this._submittedId = null;
    this._standing = null;
    this._boardNotice = '';

    if (this.bootTitle) {
      this.bootTitle.textContent = meta.stageTitle ?? meta.title;
    }
    if (this.menuTitle) {
      this.menuTitle.textContent = meta.stageTitle ?? meta.title;
    }
    if (this.menuSubtitle) this.menuSubtitle.textContent = meta.subtitle;
    if (this.menuEyebrow) this.menuEyebrow.textContent = meta.eyebrow;
    if (this.primaryButton) this.primaryButton.textContent = 'Press start';

    this.primaryButton?.addEventListener('click', () => this.onPrimary?.());
    this.shareButton?.addEventListener('click', () => this.#shareScore());
    this.leaderboardButton?.addEventListener('click', () => this.#openBoard());
    this.playerNameInput?.addEventListener('input', () => this.#savePlayerName());
    document.addEventListener('keydown', this.#onKeyDown);

    this.settingsPanel = new SettingsPanel({
      settings: this.settings,
      onClose: () => this.settingsButton?.focus({ preventScroll: true }),
    });
    this.settingsButton?.addEventListener('click', () => this.settingsPanel.open());
  }

  setLoading(progress, label) {
    const percent = Math.round(clamp(progress, 0, 1) * 100);
    if (this.bootBar) this.bootBar.style.width = `${percent}%`;
    if (this.bootStatus && label) this.bootStatus.textContent = label;
  }

  /**
   * Focus the player-name field on the first run.
   *
   * The menu fades in off the `body[data-state]` flip, and Chrome drops a
   * programmatic focus() that lands in the same frame as that flip (the field
   * is not laid out yet, so the call is silently ignored). Waiting for the next
   * frame fixes it; the retry covers a menu that is still transitioning.
   */
  #focusNameField(attempt = 0) {
    const input = this.playerNameInput;
    if (!input) return;

    requestAnimationFrame(() => {
      // The player may have started the run before the frame arrived.
      if (this.state !== 'ready') return;

      input.focus({ preventScroll: true });
      if (document.activeElement !== input) {
        if (attempt < 4) this.#focusNameField(attempt + 1);
        return;
      }
      input.select();
    });
  }

  #readPlayerName() {
    if (!this.playerNameInput) return '';
    try {
      return localStorage.getItem(this._playerKey) ?? '';
    } catch {
      return '';
    }
  }

  #savePlayerName() {
    if (!this.playerNameInput) return;
    this.playerName = this.playerNameInput.value;
    // The name is printed on the card, so any cached render is now stale.
    this._cardBlob = null;
    this._cardKey = '';
    try {
      localStorage.setItem(this._playerKey, this.playerName);
    } catch {
      /* storage may be unavailable (private mode); the name still works this run */
    }
    // If the player renames themselves on the game-over screen, re-render the
    // card so Share can still hand over a ready-made picture.
    if (this.state === 'gameover') {
      this.#prepareShareCard(this._lastScore ?? 0, this._lastReason ?? '');
    }
  }

  /** The name shown on the share card, falling back to a generic "Player 1". */
  getPlayerName() {
    return (this.playerName || '').trim() || 'PLAYER 1';
  }

  /** The name exactly as it is printed on the card (uppercased). */
  #cardName() {
    return this.getPlayerName().toUpperCase();
  }

  #openBoard() {
    if (this.board.isOpen) return;
    // Take the menu out of the tab order while the overlay is up.
    if (this.menu) this.menu.inert = true;
    this.board.open({
      highlightId: this._submittedId,
      standing: this._standing,
      notice: this._boardNotice,
    });
  }

  #closeBoard() {
    if (!this.board.isOpen) return;
    this.board.close();
    const menuVisible =
      this.state === 'ready' ||
      this.state === 'paused' ||
      this.state === 'gameover';
    if (this.menu) this.menu.inert = !menuVisible;
    if (menuVisible) this.leaderboardButton?.focus({ preventScroll: true });
  }

  /**
   * Post the finished run to the board.
   *
   * Fire-and-forget on purpose: the game-over screen must not wait on the
   * network, and a failure only changes what the board says when it is opened.
   */
  async #submitScore(score) {
    this._submittedId = null;
    this._standing = null;
    this._boardNotice = '';
    this.#setRankBadge(null);

    if (!isConfigured()) {
      this._boardNotice = 'The leaderboard is not configured.';
      return;
    }

    const result = await submitScore({ name: this.getPlayerName(), score });
    // A restart may have cleared the screen while the request was in flight.
    if (this.state !== 'gameover') return;

    if (!result.ok) {
      this._boardNotice = result.message;
      return;
    }

    this._submittedId = result.entry?.id ?? null;
    const standing = await this.board.updateStanding(score);
    if (this.state !== 'gameover') return;

    this._standing = standing;
    this.#setRankBadge(standing);
  }

  #setRankBadge(standing) {
    if (!this.leaderboardRank) return;
    if (!standing?.rank) {
      this.leaderboardRank.hidden = true;
      this.leaderboardRank.textContent = '';
      this.leaderboardButton?.removeAttribute('aria-label');
      return;
    }
    this.leaderboardRank.textContent = `#${standing.rank}`;
    this.leaderboardRank.hidden = false;
    this.leaderboardButton?.setAttribute(
      'aria-label',
      `Leaderboard — this run ranks ${standing.rank} of ${standing.total}`,
    );
  }

  setState(state, { gameOverReason = 'fuel', deathCause = 'other', score = 0 } = {}) {
    this.state = state;
    document.body.dataset.state = state;

    const menuVisible = state === 'ready' || state === 'paused' || state === 'gameover';

    // Leaving the menu (or restarting a run) dismisses the leaderboard overlay.
    if (this.board.isOpen && !menuVisible) this.board.close();

    if (this.menu) {
      this.menu.inert = !menuVisible;
      this.menu.setAttribute('aria-hidden', String(!menuVisible));
    }
    if (this.boot) {
      this.boot.inert = state !== 'boot';
      this.boot.setAttribute('aria-hidden', String(state !== 'boot'));
    }
    if (this.hud) {
      this.hud.setAttribute('aria-hidden', String(state !== 'playing'));
    }

    if (state === 'ready') {
      this.#setMenuCopy({
        state: 'Player 1 ready',
        eyebrow: this.meta.eyebrow,
        title: this.meta.stageTitle ?? this.meta.title,
        subtitle: this.meta.subtitle,
        note: '',
        button: 'Press start',
      });
    } else if (state === 'paused') {
      this.#setMenuCopy({
        state: 'Paused',
        eyebrow: 'Simulation paused // engine idling',
        title: this.meta.pausedTitle,
        subtitle: this.meta.pausedSubtitle,
        note: 'Press Enter or click Resume to capture the mouse again.',
        button: 'Resume',
      });
    } else if (state === 'gameover') {
      // Money and fuel are their own endings; health deaths name the killer.
      const cause =
        gameOverReason === 'money'
          ? 'money'
          : gameOverReason === 'health'
            ? deathCause
            : 'other';
      const death =
        this.meta.deaths?.[cause] ??
        this.meta.deaths?.other ?? {
          title: 'you just died.',
          subtitle: '“who cares.”',
        };
      const finalScore = Math.max(0, Math.floor(score));

      this.#setMenuCopy({
        state: 'Game over',
        eyebrow: 'Game over',
        title: death.title,
        subtitle: death.subtitle,
        note: this.meta.gameOverRestartNote,
        button: 'Drive again',
        score: finalScore,
        reason: death.title,
      });

      // Get the share card ready before the player can press Share.
      this.#prepareShareCard(finalScore, death.title);
      // Post the run to the leaderboard; the rank lands on the board button.
      void this.#submitScore(finalScore);
    }

    if (menuVisible) {
      // On the first run there is no stored name yet, so point the player at the
      // name field before they hit start. Later runs keep the button focused.
      const firstRun = state === 'ready' && !this.playerName;
      if (firstRun) {
        this.#focusNameField();
      } else {
        this.primaryButton?.focus({ preventScroll: true });
      }
    } else if (this.primaryButton) {
      this.primaryButton.blur();
    }

    if (this.playerNameInput && this.playerNameInput.value !== this.playerName) {
      this.playerNameInput.value = this.playerName;
    }
  }

  showError(error) {
    this.setState('boot');
    document.body.dataset.state = 'boot';

    const message = error?.message ?? String(error);
    if (typeof window.__ktownBootError === 'function') {
      window.__ktownBootError(message);
      return;
    }

    if (this.bootStatus) this.bootStatus.textContent = 'Unable to start the game.';
    if (this.bootProgress) this.bootProgress.hidden = true;
    if (this.bootDetail) {
      this.bootDetail.hidden = false;
      this.bootDetail.textContent = `${message}\nSee the browser console for details.`;
    }
  }

  #setMenuCopy({ state, eyebrow, title, subtitle, note, button, score, reason }) {
    if (this.menuState) this.menuState.textContent = state;
    if (this.menuEyebrow) this.menuEyebrow.textContent = eyebrow;
    if (this.menuTitle) this.menuTitle.textContent = title;
    if (this.menuSubtitle) this.menuSubtitle.textContent = subtitle;
    if (this.menuNote) this.menuNote.textContent = note;
    if (this.primaryButton) this.primaryButton.textContent = button;

    if (score !== undefined && this.menuScoreValue) {
      this._lastScore = score;
      this.menuScoreValue.textContent = score.toLocaleString('en-US');
    }
    if (reason !== undefined && this.menuScoreReason) {
      this._lastReason = reason;
      this.menuScoreReason.textContent = reason;
    }
  }

  /**
   * Render the share card ahead of time so that pressing Share can hand the
   * picture straight to `navigator.share()`.
   *
   * Mobile browsers only allow `navigator.share()` while the tap's user
   * activation is still live, so the card must already exist by the time the
   * button is pressed — rendering it inside the click handler would add an
   * await gap and the share sheet would be rejected.
   */
  #prepareShareCard(score, reason) {
    const key = `${score}|${reason}|${this.#cardName()}`;
    if (key === this._cardKey) return;
    this._cardKey = key;
    this._cardBlob = null;
    this.#renderScoreCard(score, reason, this.getPlayerName())
      .then((blob) => {
        // Ignore a render that was superseded by a newer one.
        if (this._cardKey === key) this._cardBlob = blob;
      })
      .catch(() => {
        /* the card will be rendered on demand when Share is pressed */
      });
  }

  /**
   * Render the final score onto an offscreen canvas and share it as an image.
   * Uses the same arcade palette as the menu so the shared image matches the
   * on-screen game-over card.
   */
  async #shareScore() {
    const score = this._lastScore ?? 0;
    const reason = this._lastReason ?? '';
    const key = `${score}|${reason}|${this.#cardName()}`;

    // Prefer the pre-rendered card: it keeps the share call inside the tap's
    // user-activation window, which mobile browsers require.
    let blob = this._cardKey === key ? this._cardBlob : null;
    if (!blob) {
      blob = await this.#renderScoreCard(score, reason, this.getPlayerName());
    }
    if (!blob) return;

    const fileName = `k-town-craft-score-${score}.png`;
    const file = new File([blob], fileName, { type: 'image/png' });

    // On mobile / Web-Share-capable browsers, open the native share sheet with
    // the picture instead of downloading it. Feature detection keeps desktop
    // browsers (which cannot file-share) on the download path.
    const nav = navigator;
    if (nav.share && nav.canShare && nav.canShare({ files: [file] })) {
      try {
        await nav.share({
          files: [file],
          title: 'K-Town Craft — Street 60',
          text: `I scored ${score.toLocaleString('en-US')} on K-Town Craft, Street 60, Khartoum!`,
        });
        return;
      } catch (error) {
        // AbortError means the player dismissed the sheet — nothing to do.
        if (error?.name === 'AbortError') return;
        // Any other failure falls through to a plain download.
      }
    }

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async #renderScoreCard(score, reason, playerName = '') {
    const W = 1080;
    const H = 1350;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');

    try {
      if (document.fonts?.ready) await document.fonts.ready;
    } catch {
      /* fonts are best-effort; fall back to monospace */
    }

    const pixel = "'Press Start 2P', 'Courier New', monospace";
    const term = "'VT323', 'Courier New', monospace";

    // Backdrop.
    ctx.fillStyle = '#080607';
    ctx.fillRect(0, 0, W, H);

    // Soft gold glow toward the top, like the menu screen.
    const glow = ctx.createRadialGradient(W / 2, 120, 0, W / 2, 120, 760);
    glow.addColorStop(0, 'rgba(244, 185, 66, 0.16)');
    glow.addColorStop(1, 'rgba(244, 185, 66, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);

    // Scanline texture.
    ctx.fillStyle = 'rgba(246, 230, 196, 0.025)';
    for (let y = 0; y < H; y += 3) ctx.fillRect(0, y, W, 1);

    // Frame.
    ctx.strokeStyle = '#cf2029';
    ctx.lineWidth = 14;
    ctx.strokeRect(44, 44, W - 88, H - 88);
    ctx.strokeStyle = '#f4b942';
    ctx.lineWidth = 4;
    ctx.strokeRect(70, 70, W - 140, H - 140);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';

    // Title.
    ctx.fillStyle = '#f6e6c4';
    ctx.font = `700 46px ${pixel}`;
    ctx.fillText('K-TOWN CRAFT', W / 2, 152);

    // Eyebrow.
    ctx.fillStyle = '#d8c4a2';
    ctx.font = `30px ${term}`;
    ctx.fillText('STREET 60 · KHARTOUM, SUDAN', W / 2, 204);

    // Gold rule under the title.
    ctx.strokeStyle = '#f4b942';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(W / 2 - 150, 246);
    ctx.lineTo(W / 2 + 150, 246);
    ctx.stroke();

    // Player name identity block (mirrors the score block below).
    const player = (playerName || '').trim().toUpperCase() || 'PLAYER 1';
    ctx.fillStyle = '#f4b942';
    ctx.font = `10px ${pixel}`;
    ctx.fillText('PLAYER', W / 2, 280);
    ctx.font = `34px ${term}`;
    ctx.fillText(player, W / 2, 320);

    // "FINAL SCORE" label.
    ctx.fillStyle = '#f4b942';
    ctx.font = `22px ${pixel}`;
    ctx.fillText('FINAL SCORE', W / 2, 452);

    // Big score, auto-shrunk to fit the frame and anchored to its baseline so
    // it never collides with the label above.
    const label = score.toLocaleString('en-US');
    let size = 180;
    ctx.font = `900 ${size}px ${pixel}`;
    const maxWidth = W - 200;
    while (ctx.measureText(label).width > maxWidth && size > 52) {
      size -= 6;
      ctx.font = `900 ${size}px ${pixel}`;
    }
    ctx.textBaseline = 'bottom';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.65)';
    ctx.shadowOffsetX = 6;
    ctx.shadowOffsetY = 6;
    ctx.fillStyle = '#ffd36a';
    ctx.fillText(label, W / 2, 690);
    ctx.shadowColor = 'transparent';
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.textBaseline = 'alphabetic';

    // Reason line.
    if (reason) {
      ctx.fillStyle = '#f13b43';
      ctx.font = `30px ${term}`;
      ctx.fillText(String(reason).toUpperCase(), W / 2, 748);
    }

    // Dashed divider.
    ctx.strokeStyle = 'rgba(246, 230, 196, 0.28)';
    ctx.lineWidth = 2;
    ctx.setLineDash([12, 10]);
    ctx.beginPath();
    ctx.moveTo(W / 2 - 260, 812);
    ctx.lineTo(W / 2 + 260, 812);
    ctx.stroke();
    ctx.setLineDash([]);

    // Street credit.
    ctx.fillStyle = '#f6e6c4';
    ctx.font = `34px ${term}`;
    ctx.fillText('BASHIR ELNIFIDI STREET', W / 2, 890);
    ctx.fillStyle = '#a08d76';
    ctx.font = `26px ${term}`;
    ctx.fillText('1:1 SCALE · KHARTOUM, SUDAN', W / 2, 936);

    // A strip of road, complete with a dashed centre line.
    const roadX = 170;
    const roadW = W - 340;
    const roadY = 1010;
    const roadH = 132;
    ctx.fillStyle = '#16110f';
    ctx.fillRect(roadX, roadY, roadW, roadH);
    ctx.strokeStyle = 'rgba(246, 230, 196, 0.5)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(roadX, roadY);
    ctx.lineTo(roadX + roadW, roadY);
    ctx.moveTo(roadX, roadY + roadH);
    ctx.lineTo(roadX + roadW, roadY + roadH);
    ctx.stroke();
    ctx.strokeStyle = '#f4b942';
    ctx.lineWidth = 6;
    ctx.setLineDash([46, 34]);
    ctx.beginPath();
    ctx.moveTo(roadX + 18, roadY + roadH / 2);
    ctx.lineTo(roadX + roadW - 18, roadY + roadH / 2);
    ctx.stroke();
    ctx.setLineDash([]);

    // Footer.
    ctx.fillStyle = '#a08d76';
    ctx.font = `26px ${term}`;
    ctx.fillText('// KHARTOUM ARCADE SYSTEM', W / 2, H - 96);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png');
    });
  }

  #onKeyDown = (event) => {
    // While the board is up it owns the keyboard: Escape backs out, and Enter
    // must not start a run behind it.
    if (this.board?.isOpen) {
      if (resolveCode(event) === 'Escape') {
        event.preventDefault();
        this.#closeBoard();
      }
      return;
    }

    if (resolveCode(event) !== 'Enter') return;
    if (event.target?.tagName === 'BUTTON') return;
    // Don't start the game while the player is typing their name.
    if (event.target?.tagName === 'INPUT') return;
    if (
      this.state !== 'ready' &&
      this.state !== 'paused' &&
      this.state !== 'gameover'
    ) {
      return;
    }

    event.preventDefault();
    this.onPrimary?.();
  };
}
